import { Avatar, Card, List, Tag, Tooltip, Typography } from 'antd';
import { formatAmount, FormatDate } from 'common/utils/common.utils';
import { useCreditLimitHistoryQuery } from '../api/creditLimitApi';
import { ArrowDownOutlined, ArrowUpOutlined, UserOutlined } from '@ant-design/icons';

type Props = {
  id: number;
};
const { Text } = Typography;

const CreditLimitHistory = ({ id }: Props) => {
  const { data, isLoading } = useCreditLimitHistoryQuery({ id });

  const result = data?.data;
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((part) => part[0].toUpperCase())
      .join('');
  };

  return (
    <div>
      <List
        loading={isLoading}
        dataSource={result}
        renderItem={(item, idx) => {
          const oldLimit = Number(item.old_credit_limit);
          const newLimit = Number(item.new_credit_limit);
          const changed = oldLimit !== newLimit;

          return (
            <Card
              key={item.id}
              hoverable
              style={{
                marginBottom: 20,
                borderRadius: 12,
                boxShadow: '0 6px 16px rgba(0,0,0,0.08)',
                borderLeft: `6px solid ${changed ? '#52c41a' : ''}`,
              }}
              bodyStyle={{ padding: '20px 24px' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                <Avatar
                  size={48}
                  style={{ backgroundColor: '#1890ff', marginRight: 16, fontWeight: 'bold' }}
                  icon={<UserOutlined />}
                >
                  {getInitials(item.updated_by)}
                </Avatar>
                <div>
                  <Text strong>{item.updated_by}</Text>
                  <br />
                  <Text type='secondary' style={{ fontSize: 12 }}>
                    {formatDate(item.updated_at)}
                  </Text>
                </div>
              </div>

              <Text style={{ display: 'block', marginBottom: 16, fontSize: 14 }}>
                {item.description}
              </Text>

              <div style={{ display: 'flex', gap: 20 }}>
                <Tooltip title='Old Credit Limit'>
                  <Tag
                    color={oldLimit === 0 ? 'default' : 'volcano'}
                    style={{ minWidth: 140, fontWeight: 'bold', fontSize: 14, padding: '6px 12px' }}
                  >
                    <ArrowDownOutlined style={{ marginRight: 6 }} />
                    {formatAmount(oldLimit)}
                  </Tag>
                </Tooltip>

                <Tooltip title='New Credit Limit'>
                  <Tag
                    color={newLimit === 0 ? 'default' : 'green'}
                    style={{ minWidth: 140, fontWeight: 'bold', fontSize: 14, padding: '6px 12px' }}
                  >
                    <ArrowUpOutlined style={{ marginRight: 6 }} />
                    {formatAmount(newLimit)}
                  </Tag>
                </Tooltip>
              </div>
            </Card>
          );
        }}
      />
    </div>
  );

  return (
    <div>
      <List
        itemLayout='vertical'
        dataSource={result}
        renderItem={(item) => {
          const oldLimit = Number(item?.old_credit_limit);
          const newLimit = Number(item?.new_credit_limit);

          return (
            <List.Item key={item.id}>
              <Text strong style={{ fontSize: 16 }}>
                {item.updated_by}
              </Text>
              <div style={{ marginTop: 4, marginBottom: 8, color: '#888' }}>
                {FormatDate(item.updated_at)}
              </div>
              <div style={{ marginBottom: 12 }}>
                <Text>{item.description}</Text>
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <Tag
                  color={oldLimit === 0 ? 'default' : 'red'}
                  style={{ minWidth: 120, textAlign: 'center' }}
                >
                  Old Limit: <Text code>{formatAmount(oldLimit)}</Text>
                </Tag>
                <Tag
                  color={newLimit === 0 ? 'default' : 'green'}
                  style={{ minWidth: 120, textAlign: 'center' }}
                >
                  New Limit: <Text code>{formatAmount(newLimit)}</Text>
                </Tag>
              </div>
            </List.Item>
          );
        }}
      />
    </div>
  );
};

export default CreditLimitHistory;
